import React, { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { useAuth } from '../AuthContext';

const PaymentStatusPage: React.FC = () => {
  const { status } = useParams<{ status: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Obtém a função para refrescar o usuário e o estado de carregamento do AuthContext
  const { refreshAuthUser, isLoading } = useAuth();

  const appointmentId = searchParams.get('appointment');

  // Efeito que é executado assim que a página carrega.
  // Ele força a re-verificação da sessão do usuário, resolvendo o problema de "logout".
  useEffect(() => {
    refreshAuthUser();
  }, []);

  useEffect(() => {
    if (status !== 'sucesso') return;

    if (typeof window !== 'undefined' && typeof (window as Window & { fbq?: (...args: unknown[]) => void }).fbq === 'function') {
      (window as Window & { fbq?: (...args: unknown[]) => void }).fbq?.('track', 'Purchase');
    }
  }, [status]);

  let statusConfig = {
    icon: <Clock className="w-16 h-16 text-gray-400" />,
    title: 'Pagamento Pendente',
    message: 'O seu agendamento está pendente de confirmação do pagamento. Iremos notificá-lo assim que for aprovado.',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
  };

  if (status === 'sucesso') {
    statusConfig = {
      icon: <CheckCircle className="w-16 h-16 text-green-500" />,
      title: 'Pagamento Aprovado!',
      message: 'Seu pagamento foi processado com sucesso. Aproveite suas sessões!',
      bgColor: 'bg-green-50',
      textColor: 'text-green-800',
    };
  } else if (status === 'falha') {
    statusConfig = {
      icon: <XCircle className="w-16 h-16 text-red-500" />,
      title: 'Falha no Pagamento',
      message: 'Houve um problema ao processar o seu pagamento. Por favor, tente novamente ou contate o suporte.',
      bgColor: 'bg-red-50',
      textColor: 'text-red-800',
    };
  }

  // Função para navegar o usuário para o painel.
  const handleReturnToDashboard = () => {
    const redirectAfterPlanPurchase = sessionStorage.getItem("redirectAfterPlanPurchase");
    if (redirectAfterPlanPurchase) {
      sessionStorage.removeItem("redirectAfterPlanPurchase");
      window.location.pathname = redirectAfterPlanPurchase;
      return;
    }

    navigate('/painel');
  };

  const getButtonLabel = () => {
    if (isLoading) {
      return <Loader2 className="w-5 h-5 animate-spin" />;
    }

    const redirectAfterPlanPurchase = sessionStorage.getItem("redirectAfterPlanPurchase");
    if (redirectAfterPlanPurchase) {
      return 'Sair';
    }

    return 'Ir para o meu Painel';
  };

  return (
    <div className={`h-screen overflow-hidden flex items-center justify-center p-4 ${statusConfig.bgColor}`}>
      <div className="bg-white p-8 sm:p-12 rounded-2xl shadow-lg text-center max-w-lg">
        <div className="mx-auto mb-6 w-20 h-20 flex items-center justify-center">
            {statusConfig.icon}
        </div>
        <h1 className={`text-3xl font-bold ${statusConfig.textColor} mb-4`}>
          {statusConfig.title}
        </h1>
        <p className="text-gray-600 mb-8 text-lg">
          {statusConfig.message}
        </p>
        {appointmentId && (
            <p className="text-sm text-gray-500 mb-8">
                ID do Agendamento: {appointmentId}
            </p>
        )}
        <div className="flex justify-center gap-4">
            {/* O botão agora usa a função de navegação e mostra um estado de carregamento */}
            <button 
              onClick={handleReturnToDashboard}
              disabled={isLoading}
              className="px-8 py-3 bg-gray-700 text-white font-semibold rounded-full hover:bg-gray-800 transition disabled:bg-gray-400 flex items-center justify-center"
            >
              {getButtonLabel()}
            </button>
            {status === 'falha' && (
                 <button onClick={() => navigate('/agendamento')} className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition">
                    Tentar Novamente
                </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusPage;

